/**
 * Marcar un campo con problema, en un formulario **pintado por el servidor**.
 * -----------------------------------------------------------------------------
 * El constructor de formularios ya lo hace para los que arma él: marca el
 * envoltorio con `is-invalid` y escribe el motivo debajo del campo. Pero la mitad
 * de las pantallas de un proyecto son un `<form method="post">` escrito en la
 * plantilla, y ahí no llegaba nada - así que cada una se inventaba su forma de
 * avisar: un recuadro arriba, un texto en rojo suelto, un `alert`.
 *
 * Que se avise distinto según cómo esté hecho el formulario por dentro es
 * exactamente lo que un theme existe para evitar.
 *
 * ```js
 * CompanyTheme.marcarError(campo, 'Este campo es obligatorio');
 * CompanyTheme.limpiarError(campo);
 * CompanyTheme.limpiarErrores(seccion);
 * ```
 *
 * ## Debajo del campo y no arriba
 *
 * Un mensaje al principio de la pantalla obliga a leerlo, acordárselo y buscar a
 * cuál de los quince campos se refiere. Debajo del que falla no hay nada que
 * buscar - y en un formulario largo, el aviso de arriba ni siquiera se ve.
 */

import { formES } from './locale-es.js';

/**
 * Lo que envuelve a un campo, sea del theme o del servidor.
 *
 * **Un `<select>` del theme no vive dentro de un `.form-outline`**: MDB le arma
 * su propia caja al lado y esconde el original. Marcar el `<select>` escondido
 * no se ve, asi que se busca primero la caja que MDB pinto -`.form-outline` con
 * su `.select-wrapper`- y si no, lo que haya alrededor.
 */
function envoltorio(control) {
  if (!control || !control.closest) return null;

  // El desplegable de MDB: su caja es hermana del `<select>` original.
  const deMdb = control.parentElement && control.parentElement.querySelector(':scope > .form-outline');

  return (
    control.closest('.form-field') ||
    control.closest('.form-outline') ||
    deMdb ||
    control.closest('.form-check') ||
    control.parentElement
  );
}

/**
 * El sitio del mensaje, creado si no estaba.
 *
 * **Fuera del `.form-outline`, no dentro.** Ese envoltorio es la caja del campo
 * -su borde, su muesca y su label flotante-, y meterle un párrafo dentro la
 * estira: el recuadro crece y el mensaje sale pintado por dentro, como si fuera
 * parte del valor. En el constructor de formularios el hueco es hermano del
 * campo, dentro del `.form-field`; aquí se hace lo mismo.
 *
 * Se crea al primer error y se queda vacío: crearlo y quitarlo haría saltar la
 * pantalla cada vez que se corrige un campo.
 */
function hueco(caja) {
  // El `.form-field` del constructor lo lleva dentro; los demás, al lado.
  const dentro = caja.classList.contains('form-field');
  const existente = dentro
    ? caja.querySelector(':scope > .form-error')
    : caja.nextElementSibling && caja.nextElementSibling.classList.contains('form-error')
      ? caja.nextElementSibling
      : null;

  if (existente) return existente;

  const error = document.createElement('p');
  error.className = 'form-error';

  if (dentro) {
    caja.appendChild(error);
  } else {
    caja.insertAdjacentElement('afterend', error);
  }

  return error;
}

/**
 * Marca el campo y escribe el motivo debajo.
 *
 * @param {HTMLElement} control El `<input>`, `<select>` o `<textarea>`.
 * @param {string} [mensaje] Sin él, el de siempre: «Este campo es obligatorio».
 * @returns {HTMLElement|null} El envoltorio marcado.
 */
export function marcarError(control, mensaje) {
  const caja = envoltorio(control);
  if (!caja) return null;

  caja.classList.add('is-invalid');
  control.setAttribute('aria-invalid', 'true');

  hueco(caja).textContent = mensaje || formES.required;

  return caja;
}

/**
 * Le quita la marca a un campo.
 *
 * @param {HTMLElement} control
 */
export function limpiarError(control) {
  const caja = envoltorio(control);
  if (!caja) return;

  caja.classList.remove('is-invalid');
  control.removeAttribute('aria-invalid');

  hueco(caja).textContent = '';
}

/**
 * Le quita la marca a todo lo que haya dentro.
 *
 * Para llamarla al empezar a comprobar: lo que se corrigió deja de estar en
 * rojo, y lo que sigue mal se vuelve a marcar en la misma pasada.
 *
 * @param {ParentNode} [root=document]
 */
export function limpiarErrores(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('.is-invalid').forEach((caja) => {
    caja.classList.remove('is-invalid');
    hueco(caja).textContent = '';
  });

  root.querySelectorAll('[aria-invalid="true"]').forEach((control) => {
    control.removeAttribute('aria-invalid');
  });
}

/**
 * Comprueba los campos obligatorios de un trozo de formulario.
 *
 * Obligatorio es lo que lleva `required` -lo que ya entiende el navegador- o
 * `data-necesario`, que además dice **cómo se llama** ese campo para poder
 * nombrarlo. Lo que está escondido no se comprueba: exigir algo que no se ve
 * deja la pantalla bloqueada sin nada que corregir.
 *
 * @param {ParentNode} root
 * @returns {HTMLElement|null} El primer campo que falta, o `null` si no falta ninguno.
 */
export function comprobarNecesarios(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return null;

  let primero = null;

  root.querySelectorAll('[required], [data-necesario]').forEach((control) => {
    if (control.closest('[hidden]')) return;
    if (String(control.value || '').trim() !== '') return;

    marcarError(control, control.dataset.necesarioMensaje || formES.required);

    if (!primero) primero = control;
  });

  return primero;
}

export default marcarError;
